<template>
  <el-dialog
    :visible="visible"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="linked-gem-dialog"
    @close="close"
  >
    <div slot="title" class="lgv-heading">
      <div class="lgv-title">{{ existingVariant ? tr("edit_title") : tr("create_title") }}</div>
      <div class="lgv-subtitle">{{ tr("subtitle") }}</div>
    </div>

    <div class="lgv-content">
      <div v-if="sourceReward" class="lgv-source">
        <img
          v-if="sourceReward.img"
          :src="sourceReward.img"
          :alt="sourceReward.reward_en || sourceReward.reward"
          class="lgv-image"
        />
        <div v-else class="lgv-image lgv-image-placeholder">
          <i class="el-icon-present"></i>
        </div>
        <div class="lgv-source-copy">
          <div class="lgv-name">{{ sourceReward.reward_en || sourceReward.reward }}</div>
          <div class="lgv-source-meta">
            <span class="lgv-badge lgv-badge-linked">
              <i class="el-icon-link"></i> {{ tr("provider_linked") }}
            </span>
            <span class="lgv-coin-kept">
              <i class="el-icon-circle-check"></i> {{ tr("coin_version_kept") }}
            </span>
          </div>
        </div>
      </div>

      <div class="lgv-field">
        <label for="linked-gem-cost">
          <span class="lgv-required">*</span>{{ tr("gem_cost") }}
        </label>
        <el-input
          id="linked-gem-cost"
          v-model.number="gemCost"
          type="number"
          :min="1"
          :step="1"
          inputmode="numeric"
          :placeholder="tr('gem_cost_placeholder')"
          @input="clearValidation"
          @keydown.native="preventNonIntegerKey"
        >
          <img slot="prefix" :src="gemIcon" alt="" class="lgv-gem-icon" />
          <template slot="append">{{ $t("rewards.gems") }}</template>
        </el-input>
        <div class="lgv-help">{{ tr("gem_cost_help") }}</div>
      </div>
    </div>

    <div slot="footer" class="lgv-footer">
      <el-button class="lgv-cancel-action" @click="close">{{ $t("global.CANCEL") }}</el-button>
      <el-button class="lgv-primary-action" type="primary" :loading="saving" @click="save">
        {{ existingVariant ? $t("global.SAVE") : tr("create_action") }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { validateLinkedGemVariant } from "@/utils/rewardVariants";
import gemIcon from "@/assets/icons/ic_flat_gem.png";

export default {
  name: "LinkedGemVariantDialog",
  props: {
    visible: { type: Boolean, default: false },
    sourceReward: { type: Object, default: null },
    masterReward: { type: Object, default: null },
    existingVariant: { type: Object, default: null },
  },
  data() {
    return {
      gemIcon,
      gemCost: null,
      saving: false,
      validation: { valid: false, errors: [], warnings: [] },
    };
  },
  computed: {
    validationMessage() {
      const error = this.validation.errors && this.validation.errors[0];
      return error ? this.tr(`errors.${error}`) : this.tr("save_failed");
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.gemCost = this.existingVariant ? Number(this.existingVariant.gems) : null;
        this.validation = { valid: false, errors: [], warnings: [] };
      }
    },
  },
  methods: {
    tr(key) {
      return this.$t(`rewards.linked_gem_variant.${key}`);
    },
    close() {
      if (!this.saving) this.$emit("update:visible", false);
    },
    runValidation() {
      return validateLinkedGemVariant(this.sourceReward, this.masterReward, this.gemCost);
    },
    clearValidation() {
      this.validation = { valid: false, errors: [], warnings: [] };
    },
    preventNonIntegerKey(event) {
      if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
        event.preventDefault();
      }
    },
    async save() {
      this.validation = this.runValidation();
      if (!this.validation.valid) {
        this.$message.error(this.validationMessage);
        return;
      }
      this.saving = true;
      try {
        const result = await this.$store.dispatch("reward/saveLinkedGemVariant", {
          sourceReward: this.sourceReward,
          existingVariant: this.existingVariant,
          gems: Number(this.gemCost),
        });
        this.$message.success(this.existingVariant ? this.tr("updated") : this.tr("created"));
        this.$emit("saved", result);
        this.$emit("update:visible", false);
      } catch (error) {
        const code = error && error.message;
        this.$message.error(code ? this.tr(`errors.${code}`) : this.tr("save_failed"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.el-dialog.linked-gem-dialog {
  width: 640px !important;
  max-width: calc(100vw - 32px);
  border-radius: 12px;
  overflow: hidden;
  color: #1a1a2e;
  font-family: Lato, "Kanit", Arial, sans-serif;
  box-shadow: 0 24px 60px rgba(17, 24, 39, 0.18);
}
.linked-gem-dialog .el-dialog__header {
  padding: 28px 32px 0;
}
.linked-gem-dialog .el-dialog__headerbtn {
  top: 27px;
  right: 30px;
  font-size: 24px;
}
.linked-gem-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #95989a;
  transition: color 150ms ease;
}
.linked-gem-dialog .el-dialog__headerbtn:hover .el-dialog__close,
.linked-gem-dialog .el-dialog__headerbtn:focus .el-dialog__close {
  color: #4a4a4a;
}
.linked-gem-dialog .el-dialog__body {
  padding: 0;
}
.linked-gem-dialog .el-dialog__footer {
  padding: 18px 32px 24px;
  border-top: 1px solid #eef0f5;
  background: #fff;
}
.lgv-heading {
  padding-right: 52px;
}
.lgv-content {
  padding: 26px 32px 28px;
}
.lgv-title {
  color: #1a1a2e;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.3px;
  line-height: 1.25;
}
.lgv-subtitle {
  margin-top: 5px;
  color: #77727f;
  font-size: 13px;
  line-height: 1.5;
  word-break: normal;
}
.lgv-source {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 76px;
  padding: 14px;
  border: 1px solid #ece8f2;
  border-radius: 10px;
  background: #f8f6fb;
}
.lgv-image {
  display: block;
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  border-radius: 9px;
  object-fit: cover;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}
.lgv-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa3b2;
  font-size: 24px;
}
.lgv-source-copy {
  min-width: 0;
}
.lgv-name {
  overflow: hidden;
  color: #2f2b36;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lgv-source-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.lgv-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}
.lgv-badge-linked {
  background: #e9f8ef;
  color: #23864a;
}
.lgv-coin-kept {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #77727f;
  font-size: 11px;
  font-weight: 600;
}
.lgv-coin-kept i {
  color: #6c30cc;
}
.lgv-field {
  margin-top: 24px;
}
.lgv-field label {
  display: block;
  margin-bottom: 8px;
  color: #4a4a4a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
}
.lgv-required {
  margin-right: 4px;
  color: #6c30cc;
}
.linked-gem-dialog .lgv-field .el-input__inner {
  height: 46px;
  padding-left: 44px;
  border: 1px solid #e2e4ea;
  border-radius: 10px 0 0 10px;
  color: #2f2b36;
  font-size: 16px;
  font-weight: 700;
  line-height: 46px;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}
.linked-gem-dialog .lgv-field .el-input__inner:focus {
  position: relative;
  z-index: 1;
  border-color: #6c30cc;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12);
}
.linked-gem-dialog .lgv-field .el-input-group__append {
  min-width: 72px;
  padding: 0 16px;
  border-color: #e2e4ea;
  border-radius: 0 10px 10px 0;
  background: #f7f8fb;
  color: #6f6975;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}
.linked-gem-dialog .lgv-field .el-input__prefix {
  left: 14px;
  display: flex;
  align-items: center;
  height: 46px;
}
.lgv-gem-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.linked-gem-dialog input[type="number"]::-webkit-outer-spin-button,
.linked-gem-dialog input[type="number"]::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}
.linked-gem-dialog input[type="number"] {
  -moz-appearance: textfield;
}
.lgv-help {
  margin-top: 7px;
  color: #88838e;
  font-size: 12px;
  line-height: 1.5;
  word-break: normal;
  overflow-wrap: break-word;
}
.lgv-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.linked-gem-dialog .lgv-footer .el-button {
  min-width: 112px;
  height: 44px;
  margin-left: 0;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
}
.linked-gem-dialog .lgv-cancel-action {
  border-color: #e2e4ea;
  background: #fff;
  color: #4a4a4a;
}
.linked-gem-dialog .lgv-cancel-action:hover,
.linked-gem-dialog .lgv-cancel-action:focus {
  border-color: #d6d9e0;
  background: #f7f8fb;
  color: #2f2f2f;
}
.linked-gem-dialog .lgv-primary-action,
.linked-gem-dialog .lgv-primary-action:hover,
.linked-gem-dialog .lgv-primary-action:focus {
  min-width: 166px;
  border-color: #6c30cc;
  background: #6c30cc;
  color: #fff;
}
.linked-gem-dialog .lgv-primary-action:hover,
.linked-gem-dialog .lgv-primary-action:focus {
  border-color: #5a25b0;
  background: #5a25b0;
  box-shadow: 0 6px 16px rgba(108, 48, 204, 0.18);
}

@media (max-width: 640px) {
  .linked-gem-dialog .el-dialog__header {
    padding: 22px 20px 0;
  }
  .linked-gem-dialog .el-dialog__headerbtn {
    top: 21px;
    right: 18px;
  }
  .lgv-content {
    padding: 22px 20px 24px;
  }
  .linked-gem-dialog .el-dialog__footer {
    padding: 16px 20px 20px;
  }
  .lgv-title {
    font-size: 21px;
  }
  .lgv-footer .el-button {
    flex: 1 1 0;
  }
  .linked-gem-dialog .lgv-primary-action,
  .linked-gem-dialog .lgv-footer .el-button {
    min-width: 0;
  }
}
</style>
